// MyCoursesPage.tsx (Facilitator-scoped)
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Title, Text, Card, Group,
  ActionIcon,
  Skeleton, Tooltip, Badge, ScrollArea, Box,
} from '@mantine/core';
import { IconRefresh, IconSearch, IconEye } from '@tabler/icons-react';
import { Flex, Image, Table, TextInput } from '@/components';
import { toast } from '@/components/ui/providers/AlertProvider';
import { useFacilitatorService, FacilitatorCourseView,} from '@actschurch/ccms-sdk';
import { useAuth } from '@/hooks/useEnrichedAuth';
import { EmptyState } from '../../Information/Components/EmptyState';

// Pick readable text colour (black/white) against an arbitrary hex background,
// so the status pill reads correctly regardless of the configured statusColor.
function getContrastText(hex?: string | null): string {
  if (!hex) return '#fff';
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return '#fff';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1A1B1E' : '#fff';
}

function StatusBadge({ course }: { course: FacilitatorCourseView }) {
  if (course.statusName) {
    return (
      <Badge
        variant="filled"
        style={{
          backgroundColor: course.statusColor ?? 'var(--mantine-color-gray-5)',
          color: getContrastText(course.statusColor),
        }}
      >
        {course.statusName}
      </Badge>
    );
  }
  return (
    <Badge color={course.isActive ? 'green' : 'red'} variant="light">
      {course.isActive ? 'Active' : 'Inactive'}
    </Badge>
  );
}

function ShimmerRow() {
  return (
    <Table.Tr>
      {Array.from({ length: 5 }).map((_, i) => (
        <Table.Td key={i}><Skeleton height={14} radius="sm" /></Table.Td>
      ))}
    </Table.Tr>
  );
}

export default function MyCoursesPage() {
  const navigate = useNavigate();
  const Facilitators = useFacilitatorService();
  const { user } = useAuth();

  const [courses, setCourses] = useState<FacilitatorCourseView[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // ─── Fetch — scoped to the logged-in facilitator ───────────────────────────
  const fetchCourses = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      setCourses(await Facilitators.getCourses(user.id));
    } catch (e) {
      console.error(e);
      toast.error('Error', 'Failed to load your courses.');
    } finally {
      setLoading(false);
    }
  }, [Facilitators, user?.id]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  // ─── Filtered list — name only, no description/searchKeywords on this view ──
  const filteredCourses = courses.filter((c) => {
    const term = searchTerm.toLowerCase();
    if (!term) return true;
    return (c.courseName ?? '').toLowerCase().includes(term);
  });

  // ─── Empty-state helpers ───────────────────────────────────────────────────
  const hasNoCoursesAtAll = !loading && courses.length === 0;
  const hasNoFilteredResults = !loading && courses.length > 0 && filteredCourses.length === 0;

  // ─── Render ──────────────────────────────────────────────────────────────
  return (
    <Container size="xl" py="xl">
      <Title order={2}>My Courses</Title>
      <Text mb="lg">Courses you're assigned to as a facilitator.</Text>

      <Card withBorder radius="md" padding="lg" mb="md">
        <TextInput
          placeholder="Search by course name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          radius="md"
          styles={{ input: { width: '300px' } }}
          size="sm"
        />
      </Card>

      {/* ── Table / Empty states ── */}
      <Card withBorder radius="md" padding="lg">
        <Flex justify="flex-end" gap="sm" mb="md">
          <Tooltip label="Refresh">
            <ActionIcon variant="subtle" onClick={fetchCourses} loading={loading} size="lg">
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
        </Flex>

        {hasNoCoursesAtAll ? (
          <EmptyState
            variant="no-data"
            entityLabel="courses"
          />
        ) : hasNoFilteredResults ? (
          <EmptyState
            variant="no-results"
            entityLabel="courses"
            onClearFilters={() => setSearchTerm('')}
          />
        ) : (
          <ScrollArea offsetScrollbars>
            <Table tableVariant="hoverable">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Course Name</Table.Th>
                  <Table.Th>Course Image</Table.Th>
                  <Table.Th>Color</Table.Th>
                  <Table.Th style={{ textAlign: 'center' }}>Registrations</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => <ShimmerRow key={i} />)
                  : filteredCourses.map((course) => (
                      <Table.Tr key={course.facilitatorCourseID}>
                        <Table.Td fw={600}>{course.courseName ?? '—'}</Table.Td>
                        <Table.Td>
                          {course.imageURL ? (
                            <Image
                              src={course.imageURL}
                              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                            />
                          ) : (
                            <Image
                              src={'/Course-placeholder.png'}
                              style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }}
                            />
                          )}
                        </Table.Td>
                        <Table.Td>
                          {course.courseColor ? (
                            <Group gap={6}>
                              <Box
                                w={18} h={18}
                                style={{ borderRadius: 4, backgroundColor: course.courseColor, border: '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }}
                              />
                              <Text size="sm">{course.courseColor}</Text>
                            </Group>
                          ) : '—'}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Badge variant="light" color="blue">
                            {course.activeRegistrationsCount ?? 0}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <StatusBadge course={course} />
                        </Table.Td>
                      </Table.Tr>
                    ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Card>
    </Container>
  );
}